import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="card-soft p-8 text-center" data-testid="reset-no-token">
          <h1 className="font-serif-display text-3xl">Missing reset link</h1>
          <p className="mt-3 text-sm" style={{color:"var(--text-secondary)"}}>
            This page needs a valid reset token. Please open the link from your reset email.
          </p>
          <Link to="/forgot-password" className="btn-outline text-sm mt-6 inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/confirm",
                                       { token, new_password: pw });
      setDone({ email: data.email });
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="card-soft p-8 text-center" data-testid="reset-done-card">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
               style={{background:"rgba(91,117,83,0.12)"}}>
            <CheckCircle2 className="w-8 h-8" style={{color:"var(--brand)"}}/>
          </div>
          <h1 className="font-serif-display text-3xl">Password updated</h1>
          <p className="mt-3 text-sm" style={{color:"var(--text-secondary)"}}>
            Sign in with your new password{done.email ? ` — ${done.email}` : ""}.
          </p>
          <Link to="/admin-login" className="btn-primary mt-6 inline-block">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <h1 className="font-serif-display text-4xl mb-2">Set a new password</h1>
      <p className="mb-8 text-sm" style={{color:"var(--text-secondary)"}}>
        Choose something you'll remember. At least 6 characters.
      </p>
      <form onSubmit={submit} className="space-y-4 card-soft p-8">
        <div>
          <label className="label-eyebrow block mb-2 inline-flex items-center gap-2">
            <Lock className="w-3.5 h-3.5"/> New password
          </label>
          <input data-testid="reset-new-password" type="password" required minLength={6}
            value={pw} onChange={e=>setPw(e.target.value)}
            autoComplete="new-password"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] focus:ring-2 focus:ring-[color:var(--brand)]/30"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Confirm password</label>
          <input data-testid="reset-confirm-password" type="password" required minLength={6}
            value={pw2} onChange={e=>setPw2(e.target.value)}
            autoComplete="new-password"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] focus:ring-2 focus:ring-[color:var(--brand)]/30"/>
        </div>
        {error && <div data-testid="reset-error" className="text-sm text-[color:var(--accent)]">{error}</div>}
        <button data-testid="reset-confirm-submit" disabled={busy} className="btn-primary w-full">
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}
